import _ from 'lodash';

import {
  PROVIDER_ACCOUNT_STATE,
  FINANCIAL_PORTAL_PRIMARY_SOURCES,
} from '../../../constants';
import { mapFields, convertCentsAmountToDollarsAmount } from './map-fields';

export const FINANCIAL_DATA_SUBTYPE_TO_YODLEE_BANK_ACCOUNT_TYPE = {
  // Bank
  'CHECKING:CHECKING_ACCOUNT': 'CHECKING',
  'CHECKING:PREPAID_CARD': 'PREPAID',
  'CHECKING:FSA': 'FSA',
  'CHECKING:COMMUTER': 'COMMUTER',
  'SAVINGS:SAVINGS_ACCOUNT': 'SAVINGS',
  'SAVINGS:MONEY_MARKET': 'MONEY_MARKET',
  'SAVINGS:CD': 'CD',
  'SAVINGS:OTHER_SAVINGS': 'OTHER',
  'RETIREMENT:IRA': 'IRA',
  // Bill
  'BILL:OTHER_BILL': 'BILLS',
  // Credit card
  'CREDIT_CARD:CREDIT_CARD': 'CREDIT',
  'CREDIT_CARD:STORE_CARD': 'STORE',
  'CREDIT_CARD:CHARGE_CARD': 'CHARGE',
  'CREDIT_CARD:OTHER_CARD': 'OTHER',
  // Insurance
  'INSURANCE:OTHER': '',
  // Retirement
  'RETIREMENT:401K': 'EMPLOYEE_RETIREMENT_ACCOUNT_401K',
  'RETIREMENT:401K_ROTH': 'EMPLOYEE_RETIREMENT_ACCOUNT_ROTH_401K',
  'RETIREMENT:403B': 'EMPLOYEE_RETIREMENT_SAVINGS_PLAN_403B',
  'RETIREMENT:403B_ROTH': 'ROTH_403B',
  'RETIREMENT:457_PLAN': 'DEFERRED_COMPENSATION_PLAN_457',
  'RETIREMENT:401A': 'MONEY_PURCHASE_PLAN',
  'RETIREMENT:OTHER_DEFINED_CONTRIBUTION_PLAN': 'DEFINED_CONTRIBUTION_PLAN',
  'RETIREMENT:PENSION_PLAN': 'PENSION_PLAN',
  'RETIREMENT:IRA_TRADITIONAL': 'INDIVIDUAL_RETIREMENT_ACCOUNT_IRA',
  'RETIREMENT:IRA_ROTH': 'ROTH_IRA',
  'RETIREMENT:IRA_SIMPLE': 'SIMPLE_IRA',
  'RETIREMENT:IRA_SEP': 'SEP_IRA',
  'RETIREMENT:CANADA_RETIREMENT_ACCOUNT':
    'REGISTERED_RETIREMENT_SAVINGS_PLAN_RRSP',
  'RETIREMENT:OTHER_RETIREMENT': 'OTHER',
  // Investment
  'INVESTMENT:BROKERAGE': 'INDIVIDUAL',
  'INVESTMENT:EMPLOYEE_SAVINGS_PLAN': 'EMPLOYEE_SAVINGS_PLAN',
  'INVESTMENT:EMPLOYEE_STOCK_PLAN': 'RESTRICTED_STOCK_AWARD',
  'INVESTMENT:EMPLOYEE_STOCK_OPTION_PLAN': 'EMPLOYEE_STOCK_OPTION_PLAN',
  'INVESTMENT:PROFIT_SHARING_PLAN': 'PROFIT_SHARING_PLAN',
  'INVESTMENT:CORPORATE_INVESTMENT_ACCOUNT': 'CORPORATE_INVESTMENT_ACCOUNT',
  'INVESTMENT:ANNUITY': 'ANNUITY',
  'INVESTMENT:529_PLAN': 'EDUCATIONAL_SAVINGS_PLAN_529',
  'INVESTMENT:EDUCATION_SAVINGS_ACCOUNT':
    'COVERDELL_EDUCATION_SAVINGS_ACCOUNT_ESA',
  'INVESTMENT:HSA': 'HEALTH_SAVINGS_ACCOUNT_HSA',
  'INVESTMENT:HRA': 'HEALTH_REIMBURSEMENT_ARRANGEMENT_HRA',
  'INVESTMENT:CASH_MANAGEMENT_ACCOUNT': 'CASH_MANAGEMENT_ACCOUNT',
  'INVESTMENT:TRUST': 'TRUST',
  'INVESTMENT:UTMA_UGMA_MINORS_ACCOUNT': 'UNIFORM_TRANSFER_TO_MINORS_ACT_UTMA',
  'INVESTMENT:CANADA_EDUCATION_SAVINGS_PLAN':
    'REGISTERED_EDUCATION_SAVINGS_PLAN_RESP',
  'INVESTMENT:OTHER_INVESTMENT': '',
  // Loan
  'LOAN:STUDENT_LOAN': 'STUDENT_LOAN',
  'LOAN:AUTO_LOAN': 'AUTO_LOAN',
  'LOAN:MORTGAGE': 'MORTGAGE',
  'LOAN:PERSONAL_LOAN': 'PERSONAL_LOAN',
  'LOAN:LINE_OF_CREDIT': 'LINE_OF_CREDIT',
  'LOAN:INSTALLMENT_LOAN': 'INSTALLMENT_LOAN',
  'LOAN:HOME_LOAN': 'HOME_EQUITY_LINE_OF_CREDIT',
  'LOAN:OTHER_LOAN': 'OTHER',
  // Reward
  'REWARD:OTHER_REWARD': '',
};

export const NW_TYPES = {
  CHECKING: 'CHECKING',
  SAVINGS: 'SAVINGS',
  INVESTMENT: 'INVESTMENT',
  RETIREMENT: 'RETIREMENT',
  OTHER: 'OTHER',
};

export const FINANCIAL_ACCOUNT_TYPE_TO_NW_TYPE = {
  BANK: {
    CHECKING: NW_TYPES.CHECKING,
    PREPAID: NW_TYPES.CHECKING,
    CD: NW_TYPES.SAVINGS,
    FSA: NW_TYPES.SAVINGS,
    MONEY_MARKET: NW_TYPES.SAVINGS,
    OTHER: NW_TYPES.SAVINGS,
    SAVINGS: NW_TYPES.SAVINGS,
    '': NW_TYPES.SAVINGS,
    IRA: NW_TYPES.RETIREMENT,
    COMMUTER: NW_TYPES.OTHER,
  },
  INVESTMENT: {
    HEALTH_REIMBURSEMENT_ARRANGEMENT_HRA: NW_TYPES.INVESTMENT,
    MONEY_MARKET_ACCOUNT: NW_TYPES.INVESTMENT,
    ADVISORY_ACCOUNT: NW_TYPES.INVESTMENT,
    ANNUITY: NW_TYPES.INVESTMENT,
    BROKERAGE_CASH: NW_TYPES.INVESTMENT,
    BROKERAGE_CASH_OPTION: NW_TYPES.INVESTMENT,
    BROKERAGE_LINK_ACCOUNT: NW_TYPES.INVESTMENT,
    BROKERAGE_MARGIN: NW_TYPES.INVESTMENT,
    CASH_MANAGEMENT_ACCOUNT: NW_TYPES.INVESTMENT,
    CHARITABLE_GIFT_ACCOUNT: NW_TYPES.INVESTMENT,
    CORPORATE_INVESTMENT_ACCOUNT: NW_TYPES.INVESTMENT,
    COVERDELL_EDUCATION_SAVINGS_ACCOUNT_ESA: NW_TYPES.INVESTMENT,
    CUSTODIAL_ACCOUNT: NW_TYPES.INVESTMENT,
    EDUCATIONAL_SAVINGS_PLAN_529: NW_TYPES.INVESTMENT,
    EMPLOYEE_BENEFIT_PLAN: NW_TYPES.INVESTMENT,
    EMPLOYEE_SAVINGS_PLAN: NW_TYPES.INVESTMENT,
    EMPLOYEE_STOCK_OPTION_PLAN: NW_TYPES.INVESTMENT,
    EMPLOYEE_STOCK_OWNERSHIP_PLAN_ESOP: NW_TYPES.INVESTMENT,
    EMPLOYEE_STOCK_PURCHASE_PLAN_ESPP: NW_TYPES.INVESTMENT,
    ESTATE: NW_TYPES.INVESTMENT,
    HEALTH_SAVINGS_ACCOUNT_HSA: NW_TYPES.INVESTMENT,
    INDEXED_ANNUITY: NW_TYPES.INVESTMENT,
    INDIVIDUAL: NW_TYPES.INVESTMENT,
    INVESTMENT_CLUB: NW_TYPES.INVESTMENT,
    IRREVOCABLE_TRUST: NW_TYPES.INVESTMENT,
    JOINT_TENANTS_COMMUNITY_PROPERTY: NW_TYPES.INVESTMENT,
    JOINT_TENANTS_TENANCY_IN_COMMON_JTIC: NW_TYPES.INVESTMENT,
    JOINT_TENANTS_TENANTS_BY_ENTIRETY: NW_TYPES.INVESTMENT,
    JOINT_TENANTS_WITH_RIGHTS_OF_SURVIVORSHI: NW_TYPES.INVESTMENT,
    LIFE_INCOME_FUND_LIF: NW_TYPES.INVESTMENT,
    LIVING_TRUST: NW_TYPES.INVESTMENT,
    NONQUALIFIED_DEFERRED_COMPENSATION_PLAN_: NW_TYPES.INVESTMENT,
    OFFSHORE_TRUST: NW_TYPES.INVESTMENT,
    PARTNERSHIP: NW_TYPES.INVESTMENT,
    PERFORMANCE_PLAN: NW_TYPES.INVESTMENT,
    PRECIOUS_METAL_ACCOUNT: NW_TYPES.INVESTMENT,
    PROFIT_SHARING_PLAN: NW_TYPES.INVESTMENT,
    PROPRIETORSHIP: NW_TYPES.INVESTMENT,
    REGISTERED_EDUCATION_SAVINGS_PLAN_RESP: NW_TYPES.INVESTMENT,
    RESTRICTED_STOCK_AWARD: NW_TYPES.INVESTMENT,
    REVOCABLE_TRUST: NW_TYPES.INVESTMENT,
    STOCK_BASKET_ACCOUNT: NW_TYPES.INVESTMENT,
    TRUST: NW_TYPES.INVESTMENT,
    UNIFORM_GIFT_TO_MINORS_ACT_UGMA: NW_TYPES.INVESTMENT,
    UNIFORM_TRANSFER_TO_MINORS_ACT_UTMA: NW_TYPES.INVESTMENT,
    VARIABLE_ANNUITY: NW_TYPES.INVESTMENT,
    '': NW_TYPES.INVESTMENT,
    CHURCH_ACCOUNT: NW_TYPES.RETIREMENT,
    DEFERRED_COMPENSATION_PLAN_457: NW_TYPES.RETIREMENT,
    DEFINED_BENEFIT_PLAN: NW_TYPES.RETIREMENT,
    DEFINED_CONTRIBUTION_PLAN: NW_TYPES.RETIREMENT,
    EMPLOYEE_RETIREMENT_ACCOUNT_401K: NW_TYPES.RETIREMENT,
    EMPLOYEE_RETIREMENT_ACCOUNT_ROTH_401K: NW_TYPES.RETIREMENT,
    EMPLOYEE_RETIREMENT_SAVINGS_PLAN_403B: NW_TYPES.RETIREMENT,
    EMPLOYEES_PENSION_SCHEME_EPS: NW_TYPES.RETIREMENT,
    EMPLOYEES_PROVIDENT_FUND_ORGANIZATION_EP: NW_TYPES.RETIREMENT,
    GROUP_RETIREMENT_SAVINGS_PLAN_GRSP: NW_TYPES.RETIREMENT,
    INDIVIDUAL_RETIREMENT_ACCOUNT_IRA: NW_TYPES.RETIREMENT,
    KEOGH_PLAN: NW_TYPES.RETIREMENT,
    MONEY_PURCHASE_PLAN: NW_TYPES.RETIREMENT,
    MONEY_PURCHASE_RETIREMENT_PLAN_401A: NW_TYPES.RETIREMENT,
    OTHER: NW_TYPES.RETIREMENT,
    PENSION_PLAN: NW_TYPES.RETIREMENT,
    REGISTERED_RETIREMENT_SAVINGS_PLAN_RRSP: NW_TYPES.RETIREMENT,
    ROLLOVER_IRA: NW_TYPES.RETIREMENT,
    ROTH_403B: NW_TYPES.RETIREMENT,
    ROTH_CONVERSION: NW_TYPES.RETIREMENT,
    ROTH_IRA: NW_TYPES.RETIREMENT,
    SARSEP_IRA: NW_TYPES.RETIREMENT,
    SEP_IRA: NW_TYPES.RETIREMENT,
    SIMPLE_IRA: NW_TYPES.RETIREMENT,
    SPOUSAL_RETIREMENT_SAVINGS_PLAN_SRSP: NW_TYPES.RETIREMENT,
    SUBSTANTIALLY_EQUAL_PERIODIC_PAYMENTS_SE: NW_TYPES.RETIREMENT,
    EXECUTOR: NW_TYPES.OTHER,
    NON_PROFIT_ORGANIZATION_501C: NW_TYPES.OTHER,
  },
};

// Mapping of Yodlee 1.1 status messages to the legacy status codes as noted in this doc:
// https://docs.google.com/spreadsheets/d/10E2S_a26bpG-J2fVhbf2Wo2deF18B7MBmBl3IC_3PQ4/edit#gid=0
export const FINANCIAL_PORTAL_ERROR_MESSAGES_TO_PROVIDER_ACCOUNT_STATUS_CODES =
  {
    DATA_RETRIEVAL_FAILED: 504,
    INSTITUTION_DOWN: 409,
    INSTITUTION_NOT_SUPPORTED: 507,
    INSTITUTION_REQUIRES_USER_ACTION: 429,
    INVALID_CREDENTIALS: 402,
    INVALID_MFA: 523,
    MFA_REQUIRED: 518,
    NO_DATA_OR_ACCOUNT: 414,
    OTHER_ERROR: 403,
    PORTAL_LOCKED: 407,
    PRODUCT_NOT_SUPPORTED: 575,
    LOGIN_REQUIRED: 526,
  };

export const SPECIALIZED_ACCOUNT_TYPE_MAP = {
  BANK: 'bank_account_type',
  BILL: 'bill_account_type',
  CREDIT_CARD: 'credit_account_type',
  INVESTMENT: 'investment_account_type',
  LOAN: 'loan_account_type',
};

export const getSpecializedAccountTypeForSubtype = ({
  subtype,
  subtypeDetails,
}) =>
  FINANCIAL_DATA_SUBTYPE_TO_YODLEE_BANK_ACCOUNT_TYPE[
    `${subtype}:${subtypeDetails}`
  ];

export const FINANCIAL_ACCOUNT_TO_YODLEE_ACCOUNT_FIELD_MAP = {
  account_name: 'name', // display on accounts tab
  account_number_last_4: 'mask', // display on accounts tab
  account_status: 'status',
  annual_percentage_yield: null,
  available_balance: null,
  balance: (acct) => convertCentsAmountToDollarsAmount(acct.balance),
  created_at: 'createdAt',
  currency: 'currency',
  details_last_synced_at: 'updatedAt',
  extra_data: 'extra',
  id: 'id',
  interest_earned_ytd: null,
  interest_rate: null,
  is_active_sync: (account) =>
    _.get(account, 'financialPortal.accountsState') === 'RETRIEVAL_IN_PROGRESS',
  is_non_usd: (acct) => acct.currency !== 'USD',
  last_refreshed: 'lastSyncedAt', // used for display on accts tab
  last_synced_at: 'lastSyncedAt',
  nw_enabled: (acct) => !acct.isHidden, // used to toggle disabled state
  nw_type: (acct) =>
    _.get(
      FINANCIAL_ACCOUNT_TYPE_TO_NW_TYPE,
      `${acct.type}.${getSpecializedAccountTypeForSubtype(acct)}`,
    ),
  provider_account_state_id: 'financialPortal.id', // shimming portal id in here since it's used for mutations
  provider_name: 'financialPortal.marketplaceInstitution.name',
  refresh_status_code: null,
  refresh_status_message: null,
  // While there is a `transactionsSyncedAt` on FDP portals, it doesn't have the semantics we expect.
  // Since the only known use case for consuming this field is checking whether an account is syncing for the first time,
  // we can instead map this back to the portal's accounts state
  transactions_last_synced_at: (account) =>
    _.get(account, 'financialPortal.isActiveSync') ||
    ['RETRIEVAL_IN_PROGRESS', 'PARTIAL_DATA_RETRIEVED'].includes(
      _.get(account, 'financialPortal.transactionsState'),
    )
      ? null
      : // explicitly fall back to `true` so truthy checks pass
        _.get(account, 'transactionsSyncedAt') || true,
  yodlee_account_type: (acct) => _.camelCase(acct.type), // used to determine where to link from accounts tab, which icon to show on accounts tab
};

export const portalAuthStateToProviderAccountState = {
  INIT: PROVIDER_ACCOUNT_STATE.LOGIN_IN_PROGRESS,
  ERROR: PROVIDER_ACCOUNT_STATE.ERROR,
  AUTHENTICATED: PROVIDER_ACCOUNT_STATE.ACTIVE,
  USER_ACTION_REQUIRED: PROVIDER_ACCOUNT_STATE.MFA_REQUIRED,
};

const getSpecializedAccountTypesForAccount = (account) => ({
  ...Object.values(SPECIALIZED_ACCOUNT_TYPE_MAP).reduce(
    (acc, specializedAccountType) => ({
      ...acc,
      [specializedAccountType]: null,
    }),
    {},
  ),
  [SPECIALIZED_ACCOUNT_TYPE_MAP[account.type]]:
    getSpecializedAccountTypeForSubtype(account),
});

export const convertFinancialAccountToYodleeAccount = (financialAccount) => ({
  ...mapFields(financialAccount, FINANCIAL_ACCOUNT_TO_YODLEE_ACCOUNT_FIELD_MAP),
  ...getSpecializedAccountTypesForAccount(financialAccount),
});

const errorPriorities = {
  HIGH: 'HIGH_PRIORITY',
  NORMAL: 'NORMAL_PRIORITY',
  MUTED: 'MUTED_ERROR',
};

const getProviderIdFromPortal = (portal) => {
  return portal?.marketplaceInstitution?.id;
};

const getIavSupportedAccountsFromPortal = (portal) => {
  const accounts = _.get(portal, 'accounts', []);
  return accounts.filter(
    (account) =>
      account.type === 'BANK' &&
      ((account.subtype === 'SAVINGS' &&
        account.subtypeDetails === 'SAVINGS_ACCOUNT') ||
        account.subtype === 'CHECKING'),
  );
};

// https://github.com/NerdWallet/identity.nerdwallet/blob/a3f66e9d054f7c138ea78b0214bfdd84ddfb50e0/src/identity/models/provider_account_state.py#L17
// Also see field mappings here: https://nerdwallet.atlassian.net/wiki/spaces/ENG/pages/552108098/FDP+-+Legacy+Data+Migration#FDP-LegacyDataMigration-fdp_auth_state_from_identity
const getProviderAccountStateStateFromPortal = (portal) => {
  // Plaid portals in ERROR and MFA_REQ both get sent to Plaid Link, but client messaging is better for the ERROR state
  if (
    portal?.primarySource === FINANCIAL_PORTAL_PRIMARY_SOURCES.PLAID &&
    portal?.authState === 'USER_ACTION_REQUIRED'
  ) {
    return PROVIDER_ACCOUNT_STATE.ERROR;
  }

  return portalAuthStateToProviderAccountState[portal?.authState];
};

export const FINANCIAL_PORTAL_TO_YODLEE_PROVIDER_ACCOUNT_MAP = {
  accounts: (portal) =>
    (portal?.accounts ?? []).map((financialAccount) =>
      convertFinancialAccountToYodleeAccount(financialAccount),
    ),
  provider_account_state: {
    acct_profile_status: null,
    aggregation_status: null,
    // NEW fields only for FDP portals
    alert_message: 'alertMessage',
    alert_name: 'alertName',
    alert_priority: 'alertPriority',
    authenticated_at: 'createdAt',
    auto_refresh_required: (portal) =>
      _.some(
        _.get(portal, 'accounts', []),
        (account) => account.autoRefreshRequired,
      ),
    // TODO debug this, it says it should have it on portal.
    error_priority: (portal) => errorPriorities[portal.errorPriority] || '',
    // NEW field only for FDP portals
    error_message: 'errorMessage',
    error_resolutions: {
      primary_action: 'primaryClientAction',
      secondary_action: 'secondaryClientAction',
    },
    iav_verified: (portal) => {
      const iavSupportedAccounts = getIavSupportedAccountsFromPortal(portal);
      return (
        iavSupportedAccounts.length > 0 &&
        _.every(iavSupportedAccounts, (account) => account.iavVerified)
      );
    },
    id: 'id', // used for refresh functionality, show/hide functionality
    identity_id: 'identityId',
    // TODO: Where should constants like this live? Should the schema constants like this be available somehow?
    is_active_sync: 'isActiveSync',
    is_auto_refreshing: 'isAutoRefreshing', // used by accts tab to determine if refreshing, but may roll up to is_refreshing
    is_balance_refreshing: 'isBalanceRefreshing',
    is_refreshable: 'isRefreshable',
    is_refreshing: (portal) =>
      !_.get(portal, 'isActiveSync') &&
      (_.get(portal, 'isManualRefreshing') ||
        _.get(portal, 'isBalanceRefreshing') ||
        _.get(portal, 'isAutoRefreshing')),
    last_refresh_attempt: 'lastRefreshedAt',
    mfa_form: null,
    previous_state: null,
    primary_source: 'primarySource',
    provider: {
      favicon: 'marketplaceInstitution.logoUrl', // used for display
      iav_admin_deactivated: 'marketplaceInstitution.iavAdminDeactivated',
      iav_enabled: (portal) => {
        const iavSupportedAccounts = getIavSupportedAccountsFromPortal(portal);
        return (
          // If the portal has finished syncing accounts and has none, then we return it as not IAV enabled so that we don't
          // try to IAV verify a portal with no accounts.
          (iavSupportedAccounts.length > 0 || portal.isActiveSync) &&
          portal?.marketplaceInstitution?.iavEnabled
        );
      },
      is_deprecated: (portal) =>
        _.get(portal, 'marketplaceInstitution.displayStatus') === 'INACTIVE',
      id: getProviderIdFromPortal,
      // Additional field used to provide the FDP entity ID for the marketplace institution mapped to the Yodlee provider shape.
      marketplaceInstitutionId: 'marketplaceInstitution.id',
      login_form: null,
      login_url: 'marketplaceInstitution.loginUrl',
      logo: 'marketplaceInstitution.logoUrl',
      name: 'marketplaceInstitution.name', // used for display on accts tab
      provider_details: null,
      security_requirements: null,
    },
    provider_account_id: ({ sourceEntityId }) =>
      sourceEntityId ? Number(sourceEntityId) : null, // used for fix link flow
    provider_id: getProviderIdFromPortal,
    provider_name: 'marketplaceInstitution.name',
    state: getProviderAccountStateStateFromPortal,
    state_set_at: 'authStateSetAt',
    status_code: (portal) => {
      const statusCode =
        FINANCIAL_PORTAL_ERROR_MESSAGES_TO_PROVIDER_ACCOUNT_STATUS_CODES[
          portal.errorMessage
        ];
      return statusCode ? String(statusCode) : null;
    },
    status_message: null,
    sum_assets: null,
    sum_bank_balances: null,
    sum_debts: null,
    sum_investment_balances: null,
    sum_loan_balances: null,
  },
  sum_assets: null,
  sum_bank_balances: null,
  sum_debts: null,
  sum_investment_balances: null,
  sum_loan_balances: null,
};
